// /certificates/:exchangeId — Exchange Certificate viewer.
//
// North Star §6: the artefact of a completed exchange. The artist (or host,
// or admin) can view + download the SVG. The endpoint returns 409 with code
// `not_yet` if the exchange isn't completed/closed yet — we render an
// EmptyState in that case.

import { Anchor, Box, Button, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { createFileRoute,Link } from '@tanstack/react-router';
import { type ReactNode,useEffect, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { EmptyState, MotionSection, PageHero } from '../../components/ui';
import { ApiError, fetchOk } from '../../lib/api';

export const Route = createFileRoute('/certificates/$exchangeId')({
  component: CertificateViewer,
});

type CertificateDto = {
  exchange: {
    id: string;
    startDate: string;
    endDate: string;
    completedAt: number | null;
  };
  artist: {
    displayName: string;
    slug: string | null;
    city: string | null;
  };
  venue: {
    name: string;
    slug: string;
    city: string;
    country: string;
  };
  artForm: string | null;
  fingerprint: string;
  issuedAt: number;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: CertificateDto }
  | { kind: 'not_yet' }
  | { kind: 'forbidden' }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

function formatDateRangeAU(start: string, end: string): string {
  const fmt = (s: string): string => {
    const d = new Date(`${s}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };
  return `${fmt(start)} – ${fmt(end)}`;
}

function CertificateViewer() {
  const { exchangeId } = Route.useParams();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const svgUrl = `/api/certificates/${encodeURIComponent(exchangeId)}.svg`;
  const jsonUrl = `/api/certificates/${encodeURIComponent(exchangeId)}.json`;

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    void (async () => {
      try {
        const res = await fetchOk(jsonUrl, { credentials: 'include' });
        const body = (await res.json()) as CertificateDto;
        if (cancelled) return;
        setState({ kind: 'ready', data: body });
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError) {
          if (e.status === 409) {
            setState({ kind: 'not_yet' });
            return;
          }
          if (e.status === 403) {
            setState({ kind: 'forbidden' });
            return;
          }
          if (e.status === 404) {
            setState({ kind: 'not_found' });
            return;
          }
          setState({ kind: 'error', message: e.message });
          return;
        }
        setState({ kind: 'error', message: 'Network problem loading the certificate.' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jsonUrl]);

  if (state.kind === 'loading') {
    return (
      <AppShell>
        <Group justify="center" py={64}>
          <Loader />
        </Group>
      </AppShell>
    );
  }

  if (state.kind === 'not_yet') {
    return (
      <AppShell>
        <PageHero
          eyebrow="Exchange certificate"
          title="Not ready yet"
          lede="Certificates appear once an exchange is closed. That's after the stay completes and both sides have released their reviews."
        />
        <MotionSection>
          <EmptyState
            title="Exchange certificate appears once the exchange is closed."
            body="Come back after the stay completes and both reviews release."
            action={
              <Button component={Link} to="/exchanges">
                Back to exchanges
              </Button>
            }
          />
        </MotionSection>
      </AppShell>
    );
  }

  if (state.kind === 'forbidden') {
    return (
      <AppShell>
        <EmptyState
          title="This certificate isn't yours."
          body="Only the artist and the venue's host can view this certificate."
          action={
            <Button component={Link} to="/exchanges">
              Back to exchanges
            </Button>
          }
        />
      </AppShell>
    );
  }

  if (state.kind === 'not_found') {
    return (
      <AppShell>
        <EmptyState
          title="Certificate not found."
          body="The exchange this certificate belongs to may have been removed."
          action={
            <Button component={Link} to="/exchanges">
              Back to exchanges
            </Button>
          }
        />
      </AppShell>
    );
  }

  if (state.kind === 'error') {
    return (
      <AppShell>
        <EmptyState title="Couldn't load this certificate." body={state.message} />
      </AppShell>
    );
  }

  const { data } = state;
  const issuedLabel = new Date(data.issuedAt).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <AppShell>
        <PageHero
          eyebrow="Exchange certificate"
          title={`${data.artist.displayName} × ${data.venue.name}`}
          lede="A credential for a completed exchange. Save it, share it, frame it."
          action={
            <Group gap="sm">
              <Button
                component="a"
                href={svgUrl}
                download={`wwaave-exchange-certificate-${data.exchange.id}.svg`}
              >
                Download SVG
              </Button>
              <Link to="/exchanges/$id" params={{ id: data.exchange.id }} style={{ textDecoration: 'none' }}>
                <Button component="span" tabIndex={-1} variant="default">
                  Back to exchange
                </Button>
              </Link>
            </Group>
          }
        />

        <MotionSection>
          <Box
            style={{
              borderRadius: 16,
              overflow: 'hidden',
              background: 'var(--wwaave-surface-bg)',
              boxShadow: '0 12px 48px rgba(108, 35, 0, 0.12)',
              border: '1px solid var(--mantine-color-gray-2)',
            }}
          >
            <img
              src={svgUrl}
              alt={`Exchange certificate for ${data.artist.displayName} at ${data.venue.name}`}
              style={{ display: 'block', width: '100%', height: 'auto' }}
              decoding="async"
            />
          </Box>
        </MotionSection>

        <MotionSection>
          <Stack gap="xs" mt="xl">
            <Title order={3}>Certificate details</Title>
            <DetailRow label="Artist" value={data.artist.displayName} />
            <DetailRow
              label="Venue"
              value={
                <Link
                  to="/venues/$slug"
                  params={{ slug: data.venue.slug }}
                  style={{ textDecoration: 'none' }}
                >
                  <Anchor component="span" c="brand.7" fw={500}>
                    {data.venue.name}
                  </Anchor>
                </Link>
              }
            />
            <DetailRow label="Location" value={`${data.venue.city}, ${data.venue.country}`} />
            <DetailRow
              label="Dates"
              value={formatDateRangeAU(data.exchange.startDate, data.exchange.endDate)}
            />
            {data.artForm ? <DetailRow label="Art form" value={data.artForm} /> : null}
            <DetailRow
              label="Fingerprint"
              value={
                <Text component="span" ff="monospace" size="sm" c="brand.9">
                  {data.fingerprint}
                </Text>
              }
            />
            <DetailRow label="Issued" value={issuedLabel} />
          </Stack>
        </MotionSection>
    </AppShell>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Group justify="space-between" wrap="nowrap" py={6} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
      <Text c="ink.6" size="sm" fw={500}>
        {label}
      </Text>
      <Text size="sm" ta="right">
        {value}
      </Text>
    </Group>
  );
}
